import React, { useState, useEffect } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { ClipboardList, Loader2, Eye } from 'lucide-react';
import { formatBRL } from '@/lib/formatters';

const MESES = [
  { value: '01', label: 'Janeiro' }, { value: '02', label: 'Fevereiro' }, { value: '03', label: 'Março' },
  { value: '04', label: 'Abril' }, { value: '05', label: 'Maio' }, { value: '06', label: 'Junho' },
  { value: '07', label: 'Julho' }, { value: '08', label: 'Agosto' }, { value: '09', label: 'Setembro' },
  { value: '10', label: 'Outubro' }, { value: '11', label: 'Novembro' }, { value: '12', label: 'Dezembro' },
];
const ANO_ATUAL = new Date().getFullYear();
const ANOS = Array.from({ length: 4 }, (_, i) => String(ANO_ATUAL - 3 + i));

const HOJE = new Date();
const MES_ATUAL = String(HOJE.getMonth() + 1).padStart(2, '0');

const ENDPOINT_POR_TIPO = {
  compra: 'notas-fiscais',
  venda: 'notas-venda',
  servico: 'notas-servico',
  tomada: 'notas-tomadas',
  outros: 'outros-documentos',
};

const renderStatusBadge = (status) => (
  <Badge
    variant="outline"
    className={status === 'enviada' ? 'border-green-500/50 text-green-400' : 'border-amber-500/50 text-amber-400'}
  >
    {status === 'enviada' ? 'Enviada' : 'Pendente'}
  </Badge>
);

const ConferenciaMensal = ({ user, onLogout }) => {
  const company = JSON.parse(localStorage.getItem('company') || '{}');

  const [mesSelect, setMesSelect] = useState(MES_ATUAL);
  const [anoSelect, setAnoSelect] = useState(String(ANO_ATUAL));
  const mes = mesSelect && anoSelect ? `${anoSelect}-${mesSelect}` : '';

  const [loading, setLoading] = useState(false);
  const [notasCompra, setNotasCompra] = useState([]);
  const [notasVenda, setNotasVenda] = useState([]);
  const [notasServico, setNotasServico] = useState([]);
  const [notasTomadas, setNotasTomadas] = useState([]);
  const [outrosDocumentos, setOutrosDocumentos] = useState([]);

  useEffect(() => {
    if (company?.id && mes) {
      fetchDados();
    }
  }, [company?.id, mes]);

  const fetchDados = async () => {
    try {
      setLoading(true);
      const params = { empresa_id: company.id, mes };
      const [compraRes, vendaRes, servicoRes, tomadaRes, outrosRes] = await Promise.all([
        axiosInstance.get('/notas-fiscais', { params }),
        axiosInstance.get('/notas-venda', { params }),
        axiosInstance.get('/notas-servico', { params }),
        axiosInstance.get('/notas-tomadas', { params }),
        axiosInstance.get('/outros-documentos', { params }),
      ]);
      setNotasCompra(compraRes.data);
      setNotasVenda(vendaRes.data);
      setNotasServico(servicoRes.data);
      setNotasTomadas(tomadaRes.data);
      setOutrosDocumentos(outrosRes.data);
    } catch (error) {
      toast.error('Erro ao carregar conferência do mês');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocumento = async (tipo, notaId) => {
    try {
      const endpoint = ENDPOINT_POR_TIPO[tipo];
      const fileRes = await axiosInstance.get(`/${endpoint}/${notaId}/arquivo`, { responseType: 'blob' });
      const url = URL.createObjectURL(fileRes.data);
      window.open(url, '_blank');
    } catch {
      toast.error('Erro ao abrir documento');
    }
  };

  const renderCompraTable = () => (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader>
        <CardTitle>Notas de Compra ({notasCompra.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {notasCompra.length === 0 ? (
          <p className="text-sm text-zinc-500 py-4">Nenhuma nota de compra lançada neste mês</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800">
                  <TableHead>Empresa</TableHead>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead>Data NF</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Pagamento</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {notasCompra.map((nota) => (
                  <TableRow key={nota.id} className="border-zinc-800 hover:bg-zinc-800/50">
                    <TableCell className="capitalize">{nota.unidade}</TableCell>
                    <TableCell>{nota.fornecedor_nome}</TableCell>
                    <TableCell>{nota.data_nf}</TableCell>
                    <TableCell>{formatBRL(nota.valor)}</TableCell>
                    <TableCell>{nota.forma_pagamento}{nota.num_parcelas ? ` (${nota.num_parcelas}x)` : ''}</TableCell>
                    <TableCell>{renderStatusBadge(nota.status)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => handleViewDocumento('compra', nota.id)} title="Visualizar documento">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderDocTable = (tipo, titulo, lista, mensagemVazio) => (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader>
        <CardTitle>{titulo} ({lista.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {lista.length === 0 ? (
          <p className="text-sm text-zinc-500 py-4">{mensagemVazio}</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800">
                  <TableHead>Empresa</TableHead>
                  <TableHead>Arquivo</TableHead>
                  <TableHead>Valor R$</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lista.map((doc) => (
                  <TableRow key={doc.id} className="border-zinc-800 hover:bg-zinc-800/50">
                    <TableCell className="capitalize">{doc.unidade}</TableCell>
                    <TableCell>{doc.arquivo_pdf_nome}</TableCell>
                    <TableCell>{doc.valor != null ? formatBRL(doc.valor) : '—'}</TableCell>
                    <TableCell>{renderStatusBadge(doc.status)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => handleViewDocumento(tipo, doc.id)} title="Visualizar documento">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="flex min-h-screen bg-zinc-950 text-white">
      <Sidebar user={user} onLogout={onLogout} activePage="conferencia-mensal" />

      <div className="flex-1 p-8">
        <div className="space-y-6">
          <SubscriptionCard user={user} />

          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <ClipboardList className="w-8 h-8 text-orange-400" />
              Conferência Mensal
            </h1>
            <p className="text-zinc-400 mt-1">Tudo o que foi lançado em Fiscais no mês, organizado por tipo, para conferir o que falta</p>
          </div>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <Select value={mesSelect} onValueChange={setMesSelect}>
                  <SelectTrigger className="w-[160px] bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Mês" />
                  </SelectTrigger>
                  <SelectContent>
                    {MESES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={anoSelect} onValueChange={setAnoSelect}>
                  <SelectTrigger className="w-[110px] bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Ano" />
                  </SelectTrigger>
                  <SelectContent>
                    {ANOS.map((a) => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
            </div>
          ) : (
            <>
              {renderCompraTable()}
              {renderDocTable('venda', 'Notas de Venda', notasVenda, 'Nenhuma nota de venda lançada neste mês')}
              {renderDocTable('servico', 'NFs Prestado', notasServico, 'Nenhuma NF Prestado lançada neste mês')}
              {renderDocTable('tomada', 'NFs Tomado', notasTomadas, 'Nenhuma NF Tomado lançada neste mês')}
              {renderDocTable('outros', 'Outros Documentos', outrosDocumentos, 'Nenhum documento lançado neste mês')}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConferenciaMensal;
